import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { eq } from 'tio-ui/utilities';
import {
  PhHouse,
  PhBank,
  PhGraduationCap,
  PhTrendUp,
  PhRobot,
  PhCaretDown,
  PhList,
  PhX,
  PhChartLine,
  PhHandshake,
  PhStar,
  PhCompass,
  PhCertificate,
  PhBooks,
  PhUsers,
} from 'ember-phosphor-icons';
import type { DemoScenarioId } from 'tio-employee/utils/career-pathways-demo-data';

/**
 * Navigation item configuration for the reimagined navigation
 */
interface NavDropdownItem {
  icon: typeof PhHouse;
  label: string;
  description: string;
  route: string;
  stat?: string;
}

// Use discriminated union for nav items with or without dropdowns
interface NavItemWithRoute {
  id: string;
  icon: typeof PhHouse;
  label: string;
  route: string;
  dropdown?: never;
  hideFor?: DemoScenarioId[];
}

interface NavItemWithDropdown {
  id: string;
  icon: typeof PhHouse;
  label: string;
  route?: never;
  dropdown: NavDropdownItem[];
  hideFor?: DemoScenarioId[];
}

type NavItem = NavItemWithRoute | NavItemWithDropdown;

/**
 * ReimaginedNavBar - Mega-menu navigation for TIO Reimagined experience
 *
 * Features:
 * - Mega-menu dropdowns with icons, descriptions, and stats
 * - Scenario-aware visibility (ta-only hides Loans, slrp-only hides Benefits)
 * - Mobile hamburger menu with accordions
 * - Active state highlighting
 * - Keyboard accessible (tab navigation, escape to close)
 *
 * @example
 * ```gts
 * <NavBar @scenarioId={{this.selectedScenarioId}} />
 * ```
 */
interface NavBarSignature {
  Args: {
    /** Current demo scenario ID for feature visibility */
    scenarioId: DemoScenarioId;
    /** Whether the layout is in fullscreen mode (hides legacy frame) */
    isFullscreen?: boolean;
  };
  Element: HTMLElement;
}

// Navigation configuration
const NAV_ITEMS: NavItem[] = [
  {
    id: 'home',
    icon: PhHouse,
    label: 'Home',
    route: 'authenticated.reimagined.home',
  },
  {
    id: 'loans',
    icon: PhBank,
    label: 'Loans',
    hideFor: ['ta-only'],
    dropdown: [
      {
        icon: PhChartLine,
        label: 'Overview',
        description: 'See your full debt picture',
        route: 'authenticated.reimagined.loans',
      },
      {
        icon: PhHandshake,
        label: 'Employer Help',
        description: 'Track SLRP contributions',
        route: 'authenticated.reimagined.finance',
        stat: '$125/mo',
      },
      {
        icon: PhStar,
        label: 'Forgiveness',
        description: 'PSLF tracker & tools',
        route: 'authenticated.reimagined.pslf',
        stat: '84/120',
      },
      {
        icon: PhCompass,
        label: 'Strategies',
        description: 'Find your best repayment plan',
        route: 'authenticated.reimagined.plan.strategy',
      },
      {
        icon: PhChartLine,
        label: 'LRAP Discovery',
        description: 'Loan repayment assistance',
        route: 'authenticated.reimagined.lrap',
      },
    ],
  },
  {
    id: 'benefits',
    icon: PhGraduationCap,
    label: 'Benefits',
    hideFor: ['slrp-only'],
    dropdown: [
      {
        icon: PhGraduationCap,
        label: 'Overview',
        description: 'All your education benefits',
        route: 'authenticated.reimagined.benefits',
      },
      {
        icon: PhBooks,
        label: 'Tuition',
        description: 'Apply for courses',
        route: 'authenticated.reimagined.benefits',
        stat: '$5,250/yr',
      },
      {
        icon: PhCertificate,
        label: 'Certifications',
        description: 'Professional development',
        route: 'authenticated.reimagined.benefits',
        stat: '$2,500/yr',
      },
    ],
  },
  {
    id: 'career',
    icon: PhTrendUp,
    label: 'Career',
    dropdown: [
      {
        icon: PhCompass,
        label: 'Explore Careers',
        description: 'Discover paths that fit you',
        route: 'authenticated.reimagined.explore',
      },
      {
        icon: PhChartLine,
        label: 'Skills Assessment',
        description: 'Identify your strengths',
        route: 'authenticated.reimagined.skills',
      },
      {
        icon: PhStar,
        label: 'Career Score',
        description: 'Your career capital',
        route: 'authenticated.reimagined.score',
      },
      {
        icon: PhUsers,
        label: 'Success Stories',
        description: 'Learn from others',
        route: 'authenticated.reimagined.success-stories',
      },
    ],
  },
  {
    id: 'advisor',
    icon: PhRobot,
    label: 'Advisor',
    route: 'authenticated.reimagined.advisor',
  },
];

// Helper to get the first dropdown route - used for "View Dashboard" CTA
const getFirstDropdownRoute = (item: NavItem): string => {
  if (item.dropdown && item.dropdown.length > 0) {
    return item.dropdown[0]!.route;
  }
  return '';
};

export default class NavBar extends Component<NavBarSignature> {
  @tracked openDropdownId: string | null = null;
  @tracked isMobileMenuOpen = false;
  @tracked mobileExpandedId: string | null = null;

  get visibleNavItems() {
    return NAV_ITEMS.filter(
      (item) => !item.hideFor || !item.hideFor.includes(this.args.scenarioId)
    );
  }

  toggleDropdown = (id: string) => {
    if (this.openDropdownId === id) {
      this.openDropdownId = null;
    } else {
      this.openDropdownId = id;
    }
  };

  closeDropdown = () => {
    this.openDropdownId = null;
  };

  // Deferred close for links - prevents interference with LinkTo navigation
  handleLinkClick = () => {
    // Use setTimeout to close dropdown after navigation starts
    // This prevents the click handler from blocking the LinkTo transition
    setTimeout(() => {
      this.openDropdownId = null;
      this.isMobileMenuOpen = false;
      this.mobileExpandedId = null;
    }, 0);
  };

  toggleMobileMenu = () => {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    if (!this.isMobileMenuOpen) {
      this.mobileExpandedId = null;
    }
  };

  closeMobileMenu = () => {
    this.isMobileMenuOpen = false;
    this.mobileExpandedId = null;
  };

  toggleMobileExpanded = (id: string) => {
    if (this.mobileExpandedId === id) {
      this.mobileExpandedId = null;
    } else {
      this.mobileExpandedId = id;
    }
  };

  handleKeyDown = (id: string, event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.closeDropdown();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleDropdown(id);
    }
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    {{! Desktop Navigation }}
    <nav
      class="hidden md:block border-b bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700"
      role="navigation"
      aria-label="Main navigation"
      ...attributes
    >
      <div class="{{if @isFullscreen 'px-6' 'max-w-7xl mx-auto px-6'}}">
        <div class="flex items-center gap-1">
          {{#each this.visibleNavItems as |item|}}
            {{#if item.dropdown}}
              {{! Dropdown nav item }}
              <div class="relative">
                <button
                  type="button"
                  class="flex items-center gap-2 px-4 py-4 text-sm font-medium transition-colors
                    {{if
                      (eq this.openDropdownId item.id)
                      'text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/30'
                      'text-gray-700 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }}"
                  aria-expanded="{{if (eq this.openDropdownId item.id) 'true' 'false'}}"
                  aria-haspopup="true"
                  {{on "click" (fn this.toggleDropdown item.id)}}
                  {{on "keydown" (fn this.handleKeyDown item.id)}}
                >
                  {{#let item.icon as |Icon|}}
                    <Icon @size={{18}} />
                  {{/let}}
                  <span>{{item.label}}</span>
                  <PhCaretDown
                    @size={{14}}
                    class="transition-transform
                      {{if (eq this.openDropdownId item.id) 'rotate-180'}}"
                  />
                </button>

                {{#if (eq this.openDropdownId item.id)}}
                  {{! Backdrop }}
                  <button
                    type="button"
                    class="fixed inset-0 z-10"
                    aria-label="Close menu"
                    tabindex="-1"
                    {{on "click" this.closeDropdown}}
                  ></button>

                  {{! Mega-menu dropdown }}
                  <div
                    class="absolute left-0 mt-0 w-80 bg-white dark:bg-gray-900 rounded-b-lg shadow-xl border border-gray-200 dark:border-gray-700 border-t-0 z-20 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
                    role="menu"
                  >
                    <div class="p-2" role="group">
                      {{#each item.dropdown as |dropdownItem|}}
                        <LinkTo
                          @route={{dropdownItem.route}}
                          class="flex items-start gap-3 p-3 rounded-lg hover:bg-violet-50 dark:hover:bg-violet-900/30 transition-colors group"
                          role="menuitem"
                          {{on "click" this.handleLinkClick}}
                        >
                          <div
                            class="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 group-hover:bg-violet-100 dark:group-hover:bg-violet-900/50 group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors"
                          >
                            {{#let dropdownItem.icon as |DropdownIcon|}}
                              <DropdownIcon @size={{20}} />
                            {{/let}}
                          </div>
                          <div class="flex-1 min-w-0">
                            <div class="flex items-center gap-2">
                              <span
                                class="font-medium text-gray-900 dark:text-white group-hover:text-violet-900 dark:group-hover:text-violet-300"
                              >{{dropdownItem.label}}</span>
                              {{#if dropdownItem.stat}}
                                <span
                                  class="text-xs font-medium px-2 py-0.5 bg-violet-100 dark:bg-violet-900/50 text-violet-700 dark:text-violet-300 rounded-full"
                                >{{dropdownItem.stat}}</span>
                              {{/if}}
                            </div>
                            <p
                              class="text-sm text-gray-500 dark:text-gray-400 mt-0.5"
                            >{{dropdownItem.description}}</p>
                          </div>
                        </LinkTo>
                      {{/each}}
                    </div>

                    {{! CTA at bottom }}
                    <div
                      class="border-t border-gray-100 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-800/50"
                    >
                      <LinkTo
                        @route={{getFirstDropdownRoute item}}
                        class="flex items-center justify-center gap-2 w-full py-2 px-4 text-sm font-medium text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/30 rounded-lg transition-colors"
                        {{on "click" this.handleLinkClick}}
                      >
                        View
                        {{item.label}}
                        Dashboard →
                      </LinkTo>
                    </div>
                  </div>
                {{/if}}
              </div>
            {{else}}
              {{! Simple nav item }}
              <LinkTo
                @route={{item.route}}
                class="flex items-center gap-2 px-4 py-4 text-sm font-medium transition-colors text-gray-700 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                {{#let item.icon as |Icon|}}
                  <Icon @size={{18}} />
                {{/let}}
                <span>{{item.label}}</span>
              </LinkTo>
            {{/if}}
          {{/each}}
        </div>
      </div>
    </nav>

    {{! Mobile Navigation }}
    <nav
      class="md:hidden bg-white border-b border-gray-200"
      role="navigation"
      aria-label="Mobile navigation"
    >
      <div class="flex items-center justify-between px-4 py-3">
        <div class="flex items-center gap-2">
          <PhTrendUp @size={{24}} class="text-violet-600" />
          <span class="font-bold text-gray-900">TIO</span>
        </div>

        <button
          type="button"
          class="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
          aria-label="{{if this.isMobileMenuOpen 'Close menu' 'Open menu'}}"
          aria-expanded="{{if this.isMobileMenuOpen 'true' 'false'}}"
          {{on "click" this.toggleMobileMenu}}
        >
          {{#if this.isMobileMenuOpen}}
            <PhX @size={{24}} />
          {{else}}
            <PhList @size={{24}} />
          {{/if}}
        </button>
      </div>

      {{#if this.isMobileMenuOpen}}
        {{! Mobile menu overlay }}
        <button
          type="button"
          class="fixed inset-0 bg-black/50 z-40"
          aria-label="Close menu"
          {{on "click" this.closeMobileMenu}}
        ></button>

        {{! Mobile menu panel }}
        <div
          class="fixed top-0 right-0 bottom-0 w-80 max-w-[calc(100vw-48px)] bg-white z-50 shadow-xl overflow-y-auto animate-in slide-in-from-right duration-300"
        >
          <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <span class="font-bold text-gray-900">Menu</span>
            <button
              type="button"
              class="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Close menu"
              {{on "click" this.closeMobileMenu}}
            >
              <PhX @size={{20}} />
            </button>
          </div>

          <div class="py-2">
            {{#each this.visibleNavItems as |item|}}
              {{#if item.dropdown}}
                {{! Accordion nav item }}
                <div class="border-b border-gray-100">
                  <button
                    type="button"
                    class="flex items-center justify-between w-full px-4 py-3 text-left"
                    aria-expanded="{{if (eq this.mobileExpandedId item.id) 'true' 'false'}}"
                    {{on "click" (fn this.toggleMobileExpanded item.id)}}
                  >
                    <div class="flex items-center gap-3">
                      {{#let item.icon as |Icon|}}
                        <Icon @size={{20}} class="text-gray-500" />
                      {{/let}}
                      <span class="font-medium text-gray-900">{{item.label}}</span>
                    </div>
                    <PhCaretDown
                      @size={{16}}
                      class="text-gray-400 transition-transform
                        {{if (eq this.mobileExpandedId item.id) 'rotate-180'}}"
                    />
                  </button>

                  {{#if (eq this.mobileExpandedId item.id)}}
                    <div class="bg-gray-50 pb-2">
                      {{#each item.dropdown as |dropdownItem|}}
                        <LinkTo
                          @route={{dropdownItem.route}}
                          class="flex items-center gap-3 px-4 py-3 pl-12 hover:bg-gray-100 transition-colors"
                          {{on "click" this.handleLinkClick}}
                        >
                          {{#let dropdownItem.icon as |DropdownIcon|}}
                            <DropdownIcon @size={{18}} class="text-gray-400" />
                          {{/let}}
                          <div class="flex-1">
                            <div class="flex items-center gap-2">
                              <span
                                class="text-sm font-medium text-gray-700"
                              >{{dropdownItem.label}}</span>
                              {{#if dropdownItem.stat}}
                                <span
                                  class="text-xs px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded"
                                >{{dropdownItem.stat}}</span>
                              {{/if}}
                            </div>
                          </div>
                        </LinkTo>
                      {{/each}}
                    </div>
                  {{/if}}
                </div>
              {{else}}
                {{! Simple mobile nav item }}
                <LinkTo
                  @route={{item.route}}
                  class="flex items-center gap-3 px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  {{on "click" this.handleLinkClick}}
                >
                  {{#let item.icon as |Icon|}}
                    <Icon @size={{20}} class="text-gray-500" />
                  {{/let}}
                  <span class="font-medium text-gray-900">{{item.label}}</span>
                </LinkTo>
              {{/if}}
            {{/each}}
          </div>
        </div>
      {{/if}}
    </nav>
  </template>
}
